// The bell: announcements from the keepers of Valor (notifications/list), the same account-wide list on the Portal and
// inside a world. The server marks every message read whenever the list is asked for (NotificationsController moves
// notifications_last_read_at), so the list is only fetched while the sheet is open, and the city is re-read afterwards
// so its new_notifications flag — the bell's dot — clears. Announcements carry a message and a time, no title.
import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { Sheet } from "@/kit/overlays";
import { Empty, Row, RowText } from "@/kit/primitives";
import { serverNow } from "@/state/clock";
import { keys } from "@/state/queries";
import { fmtAgo, parseGameTime } from "@/game/time";

export interface Announcement { id: string; text: string; ts: number; seen: boolean }

/** notifications/list → newest first, empty messages dropped; created_at may be seconds, ms or a game time string. */
export function announcementsOf(data: unknown): Announcement[] {
  const raw = (data as { notifications?: unknown } | undefined)?.notifications;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((n: Record<string, unknown>, i: number) => ({
      id: String(n.id ?? i),
      text: String(n.text ?? ""),
      ts: typeof n.created_at === "number" ? (n.created_at > 1e12 ? n.created_at : n.created_at * 1000) : parseGameTime(String(n.created_at ?? "")),
      seen: Number(n.seen) === 1,
    }))
    .filter((n) => n.text)
    .sort((a, b) => (b.ts || 0) - (a.ts || 0));
}

export function AnnouncementsSheet({ open, onClose, wid }: { open: boolean; onClose: () => void; wid: number | null }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["notifications"], queryFn: () => api.notifications.list(), enabled: open, staleTime: 0, gcTime: 60_000 });
  const items = useMemo(() => announcementsOf(q.data), [q.data]);
  const fresh = items.filter((n) => !n.seen).length;

  // the server has marked them read: re-read the city so the bell's dot goes
  useEffect(() => {
    if (q.isSuccess && wid) void qc.invalidateQueries({ queryKey: keys.villagesOfWorld(wid) });
  }, [q.isSuccess, q.dataUpdatedAt, wid, qc]);

  return (
    <Sheet open={open} onClose={onClose} from="header" anchor=".whdr-bar" label="Announcements"
      title={<div className="ann-hd"><h3 className="eyebrow" style={{ margin: 0 }}>Announcements</h3>{fresh ? <span className="ann-new">{fresh} new</span> : null}</div>}>
      {q.isLoading ? <Empty><span className="spin" /> Reading…</Empty> : q.isError ? <Empty>Couldn't read announcements.</Empty> : !items.length ? <Empty>No announcements.</Empty> : null}
      <div className="rows">
        {items.map((n) => (
          <Row key={n.id} unread={!n.seen} className="ann-row">
            <RowText
              eyebrow={<span className="ann-eb"><span>From the keepers of Valor</span>{Number.isFinite(n.ts) ? <span className="ann-when">{fmtAgo(serverNow() - n.ts)}</span> : null}</span>}
              line={<span style={{ whiteSpace: "normal" }}>{n.text}</span>}
            />
          </Row>
        ))}
      </div>
      {items.length > 0 && <p className="ann-foot">Account-wide · the same list opens from the Portal</p>}
    </Sheet>
  );
}
